import React, { Fragment, useState, useEffect } from 'react';
import { useAlert } from 'react-alert';
import { Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { getCourseSubjects } from './../../actions/subjectActions';
import { subjectLogoUrl, placeholderImageUrl } from '../../utils/assets';
import Loader from '../Loader';
import { getSavedExamSubjects, saveExamSubjects } from '../../actions/admin/examSubjectAction';
import { confirmAlert } from 'react-confirm-alert';
import { getUserSubscription } from '../../actions/subscriptionActions';

const CourseSubjects = ({ history, match }) => {
    const alert = useAlert();
    const dispatch = useDispatch();

    // State variables for localStorage items
    const [examId, setExamId] = useState('');
    const [examName, setExamName] = useState('');
    const [examSlug, setExamSlug] = useState('');
    const [courseId, setCourseId] = useState('');
    const [categoryId, setCategoryId] = useState('');
    const [ifRegSubjects, setIfRegSubjects] = useState('');
    const [maximumSubjects, setMaximumSubjects] = useState('');
    const [universityId, setUniversityId] = useState('');
    const [subscriptionId, setSubscriptionId] = useState('');

    // Other state variables
    const [nextLoading, setNextLoading] = useState(false);
    const [nextDisabled, setNextDisabled] = useState(true);
    const [disabled, setDisabled] = useState(false);
    const [slugs, setSlugs] = useState([]);
    const [isChecked, setIsChecked] = useState([]);
    const [regSubjects, setRegSubjects] = useState([]);
    const [checkedSubjects, setCheckedSubjects] = useState([]);
    const [proceedSubjects, setProceedSubjects] = useState([]);
    const [maxSubjects, setMaxSubjects] = useState(0);
    const [filteredSubject, setFilteredSubject] = useState([]);
    const [subjectCapReached, setSubjectCapReached] = useState(false);
    const [derivedSubscription, setDerivedSubscription] = useState(null);

    // Redux state
    const { user } = useSelector((state) => state.auth);
    const { savedSubjects, isExamSubjects } = useSelector((state) => state.examSubjects);
    const { courseSubjects, csloading, isCourseSubject } = useSelector((state) => state.courseSubjects);
    const { sloading, userSubscription: activeSubscription, error: subscriptionError, userSubscriptionList: subscriptionHistory } = useSelector((state) => state.examSubscriptions);

    const userId = user?._id;
    const [subscriptionGuardPrompted, setSubscriptionGuardPrompted] = useState(false);
    const [subscriptionFetchComplete, setSubscriptionFetchComplete] = useState(false);

    // Debug Redux state changes
    useEffect(() => {
        console.log('📊 [CourseSubjects] Redux state updated:', {
            courseSubjects,
            courseSubjectsLength: courseSubjects?.length,
            csloading,
            isCourseSubject,
            filteredSubjectLength: filteredSubject?.length,
            savedSubjects,
            isExamSubjects,
            regSubjects,
            proceedSubjects,
            isChecked,
            subjectCapReached
        });
    }, [courseSubjects, csloading, isCourseSubject, filteredSubject, savedSubjects, isExamSubjects, regSubjects, proceedSubjects, isChecked, subjectCapReached]);

    // Fetch localStorage items and set them in state
    useEffect(() => {
        const examIdVal = localStorage.getItem('examId');
        const examNameVal = localStorage.getItem('examName');
        const examSlugVal = localStorage.getItem('examSlug');
        const courseIdVal = localStorage.getItem('courseId');
        const categoryIdVal = localStorage.getItem('categoryId');
        const ifRegSubjectsVal = localStorage.getItem('RegSubjects');
        const maximumSubjectsVal = localStorage.getItem('maximumSubjects');
        const universityIdVal = localStorage.getItem('universityId');
        const subscriptionIdVal = localStorage.getItem('userSubscription');

        console.log('🔍 [CourseSubjects] localStorage values:', {
            examId: examIdVal,
            examName: examNameVal,
            examSlug: examSlugVal,
            courseId: courseIdVal,
            categoryId: categoryIdVal,
            RegSubjects: ifRegSubjectsVal,
            maximumSubjects: maximumSubjectsVal,
            universityId: universityIdVal,
            userSubscription: subscriptionIdVal
        });

        setExamId(examIdVal);
        setExamName(examNameVal);
        setExamSlug(examSlugVal);
        setCourseId(courseIdVal);
        setCategoryId(categoryIdVal);
        setIfRegSubjects(ifRegSubjectsVal);
        setMaximumSubjects(maximumSubjectsVal);
        setUniversityId(universityIdVal);
        setSubscriptionId(subscriptionIdVal);
    }, []);

    // Fetch localStorage items and dispatch actions
    useEffect(() => {
        console.log('🚀 [CourseSubjects] Dispatch useEffect triggered with:', {
            examId,
            courseId,
            universityId,
            userId
        });

        if (examId) {
            console.log('📤 [CourseSubjects] Dispatching getCourseSubjects...');
            dispatch(getCourseSubjects(courseId, examId, universityId));
        } else {
            console.warn('⚠️ [CourseSubjects] examId is missing, not dispatching getCourseSubjects');
        }
        
        if (examId && userId) {
            console.log('📤 [CourseSubjects] Dispatching getUserSubscription...');
            setSubscriptionFetchComplete(false);
            const fetchPromise = dispatch(getUserSubscription(userId, examId, universityId || null));
            if (fetchPromise && typeof fetchPromise.finally === 'function') {
                fetchPromise.finally(() => setSubscriptionFetchComplete(true));
            } else {
                setSubscriptionFetchComplete(true);
            }
        } else {
            setSubscriptionFetchComplete(true);
        }
    }, [userId, dispatch, examId, courseId, universityId]);

    useEffect(() => {
        if (!examId || !userId) {
            return;
        }

        if (!subscriptionFetchComplete) {
            console.log('⏳ [CourseSubjects] Waiting for subscription fetch to finish...');
            return;
        }

        const effectiveSubscription = (activeSubscription && activeSubscription._id)
            ? activeSubscription
            : derivedSubscription;

        if (subjectCapReached) {
            console.warn('🚫 [CourseSubjects] Subject cap reached for current subscription. Redirecting to subscription plans.');
            setSubscriptionGuardPrompted(true);
            localStorage.removeItem('userSubscription');
            setSubscriptionId('');
            localStorage.setItem('pendingSubjectRegistration', '/course-subjects');
            localStorage.setItem('forceSubscriptionRenewal', 'true');
            alert.info('You have used all available subject slots for this subscription. Please purchase a new plan to continue.');
            history.replace('/subscription-plans');
            return;
        }

        if (subscriptionError && !sloading) {
            alert.error(subscriptionError);
        }

        if (sloading) {
            return;
        }

        // Prefer API match; fall back to localStorage id present in history (avoids false renewals)
        const storedSubscriptionId = subscriptionId || localStorage.getItem('userSubscription');
        const historyMatch = Array.isArray(subscriptionHistory) && storedSubscriptionId
            ? subscriptionHistory.find((entry) => String(entry?._id || '') === String(storedSubscriptionId))
            : null;
        const resolvedSubscription = (effectiveSubscription && effectiveSubscription._id)
            ? effectiveSubscription
            : (historyMatch && historyMatch._id ? historyMatch : null);

        if (resolvedSubscription && resolvedSubscription._id) {
            if (subscriptionGuardPrompted) {
                setSubscriptionGuardPrompted(false);
            }

            if (subscriptionId !== resolvedSubscription._id) {
                console.log('✅ [CourseSubjects] Active subscription detected:', resolvedSubscription._id);
                setSubscriptionId(resolvedSubscription._id);
                localStorage.setItem('userSubscription', resolvedSubscription._id);
            }

            localStorage.removeItem('forceSubscriptionRenewal');
            const pendingRedirect = localStorage.getItem('pendingSubjectRegistration');
            if (pendingRedirect) {
                console.log('🧹 [CourseSubjects] Clearing pendingSubjectRegistration flag');
                localStorage.removeItem('pendingSubjectRegistration');
            }
        } else if (!subscriptionGuardPrompted) {
            // Don't bounce to plans on a transient API failure if we still have a stored id
            if (subscriptionError && storedSubscriptionId) {
                console.warn('⚠️ [CourseSubjects] Subscription lookup failed but stored id exists; staying on page.');
                return;
            }

            console.warn('⚠️ [CourseSubjects] No active subscription found for exam:', examId);
            setSubscriptionGuardPrompted(true);
            localStorage.removeItem('userSubscription');
            setSubscriptionId('');
            localStorage.setItem('pendingSubjectRegistration', '/course-subjects');
            localStorage.removeItem('forceSubscriptionRenewal');
            alert.info('You need an active subscription for this exam before you can register subjects.');
            history.replace('/subscription-plans');
            return;
        }

        const exhaustedSubscription = resolvedSubscription && Array.isArray(subscriptionHistory)
            ? subscriptionHistory.find((subscription) => {
                if (!subscription) {
                    return false;
                }

                const sameRecord = String(subscription?._id || '') === String(resolvedSubscription?._id || '');
                if (!sameRecord) {
                    return false;
                }

                const subscriptionExamId = subscription?.exam?._id || subscription?.exam;
                const subscriptionUniversityId = subscription?.university?._id || subscription?.university || null;

                const isExamMatch = examId ? String(subscriptionExamId) === String(examId) : true;
                const isUniversityMatch = universityId ? String(subscriptionUniversityId) === String(universityId) : !subscriptionUniversityId;

                if (!isExamMatch || !isUniversityMatch) {
                    return false;
                }

                return Boolean(subscription?._allSubjectsUsed);
            })
            : null;

        if (exhaustedSubscription) {
            console.warn('🚫 [CourseSubjects] Subscription exists but all subjects already registered. Forcing re-subscription.');
            setSubscriptionGuardPrompted(true);
            localStorage.removeItem('userSubscription');
            setSubscriptionId('');
            localStorage.setItem('pendingSubjectRegistration', '/course-subjects');
            localStorage.setItem('forceSubscriptionRenewal', 'true');
            alert.info('Your current subscription has registered the maximum subjects. Please purchase a new plan to add more subjects.');
            history.replace('/subscription-plans');
            return;
        }
    }, [activeSubscription, derivedSubscription, subscriptionError, sloading, subscriptionGuardPrompted, history, examId, userId, subscriptionId, alert, subscriptionFetchComplete, subscriptionHistory, universityId, subjectCapReached]);

    // Fetch saved exam subjects (guard until userId is available to avoid 400 on undefined)
    useEffect(() => {
        if (userId) {
            console.log('📤 [CourseSubjects] Dispatching getSavedExamSubjects with userId:', userId);
            dispatch(getSavedExamSubjects(userId));
        } else {
            console.warn('⚠️ [CourseSubjects] Skipping getSavedExamSubjects, userId not yet available');
        }
    }, [isCourseSubject, userId, dispatch]);

    // Scroll to the top when the component mounts
    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    // After payment, only prompt the student to pick subjects — never auto-register.
    useEffect(() => {
        const justPurchased = localStorage.getItem('subscriptionJustPurchased') === 'true';
        if (!justPurchased) return;

        localStorage.removeItem('subscriptionJustPurchased');
        localStorage.removeItem('paymentCompleted');
        alert.success('Payment successful. Select your subjects below to continue.');
    }, [alert]);

    // Enable Next button only after year questions API responds
    useEffect(() => {
        setNextDisabled(true);
        setNextLoading(true);

        const _api_path = (process.env.REACT_APP_API_BASE_URL || '/api/v1').replace(/\/+$/, '');
        fetch(`${_api_path}/get-year-questions`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({}) })
            .then((res) => res.json())
            .then(() => {
                setNextDisabled(false);
                setNextLoading(false);
            })
            .catch((err) => {
                setNextDisabled(true);
                setNextLoading(false);
                console.error('Failed to fetch year questions:', err);
            });
    }, []);

    // Process saved exam subjects
    useEffect(() => {
        console.log('🔍 [CourseSubjects] Processing savedSubjects');
        console.log('  📋 savedSubjects:', savedSubjects);
        console.log('  📋 examId:', examId);
        console.log('  📋 universityId:', universityId);
        console.log('  📋 examSlug:', examSlug);

        let capReachedForExam = false;
        const normalizedExamId = examId ? String(examId) : null;
        const normalizedUniversityId = universityId ? String(universityId) : null;
        const activePlanId = activeSubscription?._id || subscriptionId || localStorage.getItem('userSubscription');

        let matchedRecord = null;
        let fallbackRecord = null;
        let registeredCount = 0;
        let examMaximum = 0;

        if (Array.isArray(savedSubjects) && savedSubjects.length > 0) {
            for (const entry of savedSubjects) {
                const entryExamId = entry?.exam?._id || entry?.exam;
                if (normalizedExamId && String(entryExamId || '') !== normalizedExamId) {
                    continue;
                }

                const entryPlanId = entry?.subscriptionPlan?._id || entry?.subscriptionPlan || null;
                const entryUniversityId =
                    entry?.subscriptionPlan?.university?._id ||
                    entry?.subscriptionPlan?.university ||
                    null;

                if (normalizedUniversityId && String(entryUniversityId || '') !== normalizedUniversityId) {
                    continue;
                }

                if (!fallbackRecord) {
                    fallbackRecord = entry;
                }

                if (activePlanId && entryPlanId && String(entryPlanId) === String(activePlanId)) {
                    matchedRecord = entry;
                    break;
                }
            }
        } else {
            console.log('  ⚠️ No savedSubjects or empty array');
        }

        const recordToUse = matchedRecord || (!activePlanId ? fallbackRecord : null);

        if (recordToUse) {
            console.log('  ✅ Using savedSubjects record:', {
                recordId: recordToUse?._id,
                planId: recordToUse?.subscriptionPlan?._id || recordToUse?.subscriptionPlan,
            });

            const requiresUniversityFilter = examSlug === 'post-utme';
            const processedIds = recordToUse?.subjectsProcessed?.map((subject) => subject) || [];
            setProceedSubjects(processedIds);
            console.log('  ✅ Set proceedSubjects:', processedIds);

            if (requiresUniversityFilter && universityId) {
                const filteredSubjects = recordToUse?.subjects
                    ?.filter((subject) => {
                        const uni = subject?.subject?.university;
                        const matches = uni && Array.isArray(uni) && uni.length > 0 && uni[0] === universityId;
                        console.log(`    📌 Subject ${subject?.subject?.title}: uni=`, uni, '| matches:', matches);
                        return matches;
                    })
                    ?.map((subject) => subject?.subject?._id);
                console.log('  ✅ Set regSubjects (with university filter):', filteredSubjects);
                setRegSubjects(filteredSubjects || []);
            } else {
                const allSubjectIds = recordToUse?.subjects?.map((subject) => subject?.subject?._id) || [];
                console.log('  ✅ Set regSubjects (no university filter):', allSubjectIds);
                setRegSubjects(allSubjectIds);
            }

            registeredCount = Array.isArray(recordToUse?.subjectsProcessed)
                ? recordToUse.subjectsProcessed.length
                : Array.isArray(recordToUse?.subjects)
                    ? recordToUse.subjects.length
                    : 0;

            examMaximum = Number(recordToUse?.exam?.maximumSubjects) || 0;

            if (matchedRecord && examMaximum > 0 && registeredCount >= examMaximum) {
                capReachedForExam = true;
            }
        } else {
            setProceedSubjects([]);
            setRegSubjects([]);
        }

        const fallbackSubscription = (() => {
            if (!recordToUse || !recordToUse.subscriptionPlan) {
                return null;
            }

            const plan = recordToUse.subscriptionPlan;
            const planId = plan?._id || plan?.id || plan;
            if (!planId) {
                return null;
            }

            if (plan?.status === false) {
                return null;
            }

            const planExamId = plan?.exam?._id || plan?.exam || recordToUse?.exam?._id || recordToUse?.exam || null;
            if (normalizedExamId && planExamId && String(planExamId) !== normalizedExamId) {
                return null;
            }

            const planUniversityId = plan?.university?._id || plan?.university || recordToUse?.university?._id || recordToUse?.university || null;
            if (normalizedUniversityId && planUniversityId && String(planUniversityId) !== normalizedUniversityId) {
                return null;
            }

            const expiry = plan?.expireAt || plan?.subscription?.expireAt || recordToUse?.expireAt || null;
            if (expiry) {
                const expiryDate = new Date(expiry);
                if (!Number.isNaN(expiryDate.getTime()) && expiryDate < new Date()) {
                    return null;
                }
            }

            const isCapReached = examMaximum > 0 && registeredCount >= examMaximum;

            return {
                ...plan,
                _id: String(planId),
                exam: plan?.exam || recordToUse?.exam || null,
                university: plan?.university || recordToUse?.university || null,
                expireAt: expiry,
                _allSubjectsUsed: isCapReached,
            };
        })();

        setDerivedSubscription((prev) => {
            if (!fallbackSubscription && !prev) {
                return prev;
            }

            if (fallbackSubscription && prev) {
                if (
                    fallbackSubscription._id === prev._id &&
                    fallbackSubscription._allSubjectsUsed === prev._allSubjectsUsed &&
                    (fallbackSubscription?.status ?? true) === (prev?.status ?? true) &&
                    (fallbackSubscription?.expireAt || null) === (prev?.expireAt || null)
                ) {
                    return prev;
                }
            }

            return fallbackSubscription || null;
        });

        setSubjectCapReached(capReachedForExam);
    }, [isExamSubjects, examId, universityId, examSlug, savedSubjects, subscriptionId, activeSubscription?._id]);

    // Set filteredSubject based on universityId.
    // Only apply university filtering for exams that actually scope subjects by university (e.g. post-utme).
    // For other exams (e.g. WASSCE) subjects come with an empty university array, so we should NOT filter them out.
    useEffect(() => {
        console.log('🔍 [CourseSubjects] Filtering subjects. universityId:', universityId, 'examSlug:', examSlug);
        console.log('📋 [CourseSubjects] courseSubjects to filter (length):', courseSubjects?.length);

        if (!courseSubjects || courseSubjects.length === 0) {
            console.log('⚠️ [CourseSubjects] No courseSubjects to filter');
            return;
        }

        // Exams that require university scoping
        const requiresUniversityFilter = examSlug === 'post-utme';

        if (!requiresUniversityFilter) {
            // Bypass filtering entirely
            console.log('ℹ️ [CourseSubjects] Exam does not require university filtering. Using all subjects.');
            setFilteredSubject(courseSubjects);
            return;
        }

        if (requiresUniversityFilter && universityId) {
            const filteredCourseSubjects = courseSubjects.filter(cs => {
                const hasUniversity = cs.university && Array.isArray(cs.university) && cs.university.length > 0;
                const includesUni = hasUniversity && cs.university.includes(universityId);

                if (cs.title) {
                    console.log(`  📌 Subject "${cs.title}": university=`, cs.university, '| includes?', includesUni);
                }
                return includesUni;
            });

            if (filteredCourseSubjects.length === 0) {
                console.warn('⚠️ [CourseSubjects] University filter returned 0 subjects. Falling back to all subjects.');
                setFilteredSubject(courseSubjects);
            } else {
                console.log('✅ [CourseSubjects] Filtered result length:', filteredCourseSubjects.length);
                setFilteredSubject(filteredCourseSubjects);
            }
        } else {
            console.log('ℹ️ [CourseSubjects] Missing universityId or filter not required; using all subjects.');
            setFilteredSubject(courseSubjects);
        }
    }, [universityId, courseSubjects, examSlug]);

    useEffect(() => {
        if (!filteredSubject || filteredSubject.length === 0) {
            return;
        }

        const validIds = new Set(filteredSubject.map((subject) => subject?._id));

        const sanitizedRegSubjects = regSubjects.filter((id) => validIds.has(id));
        const sanitizedProceedSubjects = proceedSubjects.filter((id) => validIds.has(id));

        const regChanged =
            sanitizedRegSubjects.length !== regSubjects.length ||
            sanitizedRegSubjects.some((id, index) => id !== regSubjects[index]);
        const proceedChanged =
            sanitizedProceedSubjects.length !== proceedSubjects.length ||
            sanitizedProceedSubjects.some((id, index) => id !== proceedSubjects[index]);

        if (regChanged) {
            setRegSubjects(sanitizedRegSubjects);
        }

        if (proceedChanged) {
            setProceedSubjects(sanitizedProceedSubjects);
        }
    }, [filteredSubject, regSubjects, proceedSubjects]);

    // Process course subjects and set state
    useEffect(() => {
        let subjects = filteredSubject;

        console.log('🔄 [CourseSubjects] Processing subjects for isChecked initialization');
        console.log('  📋 filteredSubject length:', subjects?.length);
        console.log('  📋 regSubjects:', regSubjects);
        console.log('  📋 current isChecked:', isChecked);

        if (subjects && subjects.length > 0) {
            const resolvedMaximum = Number(subjects[0]?.exam?.maximumSubjects) || 0;
            setMaxSubjects(resolvedMaximum);
            const mandatorySubjects = subjects
                .filter(cs => cs.mandetory === true)
                .map(cs => cs._id);

            console.log('  ✅ mandatorySubjects:', mandatorySubjects);

            // Merge mandatory subjects + previously registered subjects (regSubjects)
            // regSubjects contains IDs of subjects user already registered
            const mergedSubSet = new Set([...mandatorySubjects, ...regSubjects]);
            const mergedSub = Array.from(mergedSubSet);

            console.log('  ✅ mergedSub (final isChecked):', mergedSub);

            setIsChecked(mergedSub);
            const SubjectsWithStyled = generateStyledSubjects(mergedSub);
            setCheckedSubjects(SubjectsWithStyled);
        }
    }, [isCourseSubject, regSubjects, filteredSubject]);

    // Generate styled subjects for display
    const generateStyledSubjects = (subjectIds) => {
        return subjectIds
            .map((id, index) => {
                const subject = filteredSubject.find((c) => c?._id === id);

                if (!subject) {
                    return null;
                }

                const isMandatory = subject?.mandetory === true;
                return (
                    <span key={id} style={{ fontWeight: isMandatory ? 'bold' : 'normal', color: isMandatory ? 'red' : 'black' }}>
                        <strong>({index + 1}) </strong>{subject.title}{' '}
                    </span>
                );
            })
            .filter(Boolean);
    };

    // Handle checkbox change
    const handleCheckboxChange = (event) => {
        const currentChecked = isChecked;
        const maxAllowed = Number(maxSubjects) || 0;
        if (maxAllowed > 0 && currentChecked.length >= maxAllowed && !currentChecked.includes(event.target.value)) {
            const selectedSubjectsInfo = generateStyledSubjects(currentChecked);
            setCheckedSubjects(selectedSubjectsInfo);
            confirmAlert({
                title: 'Error',
                message: (
                    <div>
                        <p>
                            You have reached the maximum limit (<span style={{ fontWeight: 'bold', color: 'black' }}>{maxAllowed}</span>) of selected subjects.
                        </p>
                        <p>
                            Selected Subjects: {selectedSubjectsInfo}
                        </p>
                        <p>
                            If you want to select another subject, please uncheck the selected subject(s) and choose another subject(s).
                        </p>
                        <p className="bg-danger" style={{ color: 'red' }}>
                            Note: Mandatory subjects cannot be deselected.
                        </p>
                    </div>
                ),
                buttons: [
                    {
                        label: 'OK',
                        onClick: () => { },
                    },
                ],
            });
            return;
        }

        let newArray = [...isChecked, event.target.value];
        if (isChecked.includes(event.target.value) && filteredSubject?.find((c) => c?._id === event.target.value)?.mandetory === false) {
            newArray = newArray.filter((day) => day !== event.target.value);
        }

        setIsChecked(newArray);
        const selectedSubjectsInfo = generateStyledSubjects(newArray);
        setCheckedSubjects(selectedSubjectsInfo);
        if (event.target.checked === true) {
            setDisabled(false);
        }

        let newArraySlug = [...slugs, event.target.dataset.slug];
        if (slugs.includes(event.target.dataset.slug)) {
            newArraySlug = newArraySlug.filter((day) => day !== event.target.dataset.slug);
        }
        setSlugs(newArraySlug);
    };

    // Handle form submission
    const submitHandler = (e) => {
        e.preventDefault();
        console.log('🚀 [submitHandler] Called');
        console.log('  📋 isChecked length:', isChecked?.length);
        console.log('  📋 maximumSubjects (localStorage):', maximumSubjects);
        console.log('  📋 maxSubjects (from exam):', maxSubjects);
        console.log('  📋 isChecked array:', isChecked);

        // Use maxSubjects (from exam data) or fall back to maximumSubjects (from localStorage)
        const maxAllowed = Number(maxSubjects) || Number(maximumSubjects) || 0;
        const requiresFlexibleSelection = examSlug === 'post-utme';
        const minRequired = requiresFlexibleSelection ? 1 : maxAllowed;
        const remainingSub = maxAllowed - isChecked?.length;

        console.log('  📋 maxAllowed:', maxAllowed);
        console.log('  📋 remainingSub:', remainingSub);

        if (isChecked?.length < minRequired) {
            const missingCount = Math.max(minRequired - (isChecked?.length || 0), 0);
            const baseMessage = requiresFlexibleSelection
                ? 'You need to select at least one subject to continue.'
                : `You have selected ${isChecked?.length || 0} subject(s), but you need to select ${minRequired}.`;

            confirmAlert({
                title: 'Incomplete Selection',
                message: (
                    <div>
                        <p style={{ color: 'orange', fontWeight: 'bold' }}>
                            You have not selected enough subjects!
                        </p>
                        <p>{baseMessage}</p>
                        {missingCount > 0 && !requiresFlexibleSelection && (
                            <p>Please select {missingCount} more subject(s) to proceed.</p>
                        )}
                        {isChecked?.length > 0 && (
                            <p>
                                Currently selected: {checkedSubjects}
                            </p>
                        )}
                    </div>
                ),
                buttons: [
                    {
                        label: 'OK',
                        onClick: () => { },
                    },
                ],
            });
            return;
        }

        // Check if candidate has not selected enough subjects
        if (!requiresFlexibleSelection && isChecked?.length < maxAllowed && proceedSubjects?.length !== maxAllowed) {
            console.log('⚠️ [submitHandler] Not enough subjects selected!');
            console.log('  📋 Selected:', isChecked?.length, '| Required:', maxAllowed);
            confirmAlert({
                title: 'Incomplete Selection',
                message: (
                    <div>
                        <p style={{ color: 'orange', fontWeight: 'bold' }}>
                            You have not selected enough subjects!
                        </p>
                        <p>
                            You have selected <strong>{isChecked?.length}</strong> subjects, but you need to select <strong>{maxAllowed}</strong> subjects.
                        </p>
                        <p>
                            Please select {maxAllowed - isChecked?.length} more subject(s) to proceed.
                        </p>
                        {isChecked?.length > 0 && (
                            <p>
                                Currently selected: {checkedSubjects}
                            </p>
                        )}
                    </div>
                ),
                buttons: [
                    {
                        label: 'OK',
                        onClick: () => { },
                    },
                ],
            });
            return; // Prevent navigation
        }

        // Check if candidate has exceeded the maximum number of subjects
        if (isChecked?.length > maxAllowed) {
            console.log('⚠️ [submitHandler] Exceeded maximum subjects!');
            console.log('  📋 Selected:', isChecked?.length, '| Allowed:', maxAllowed);
            confirmAlert({
                title: 'Too Many Subjects',
                message: (
                    <div>
                        <p style={{ color: 'red', fontWeight: 'bold' }}>
                            You have exceeded the maximum number of subjects allowed!
                        </p>
                        <p>
                            You have selected <strong>{isChecked?.length}</strong> subjects, but only <strong>{maxAllowed}</strong> subjects are allowed.
                        </p>
                        <p>
                            Please uncheck {isChecked?.length - maxAllowed} subject(s) to proceed.
                        </p>
                        <p>
                            Selected Subjects: {checkedSubjects}
                        </p>
                        <p className="bg-danger" style={{ color: 'red' }}>
                            Note: Mandatory subjects cannot be deselected.
                        </p>
                    </div>
                ),
                buttons: [
                    {
                        label: 'OK',
                        onClick: () => { },
                    },
                ],
            });
            return; // Prevent navigation
        }

        // At this point, user has selected exactly the required number of subjects
        if (proceedSubjects?.length !== maxAllowed || requiresFlexibleSelection) {
            confirmAlert({
                title: 'Confirmation',
                message: (
                    <div>
                        <p>
                            Are you sure you want to register these <strong className="highlighted" style={{ fontWeight: 'bold', backgroundColor: 'yellow' }}>{checkedSubjects}</strong> subjects?
                        </p>
                        {proceedSubjects?.length === 0 && <>
                            <p>You have not registered any subjects yet.</p>
                            <p>
                                Click "Yes" to register and proceed.
                            </p>
                        </>}
                        {proceedSubjects?.length !== 0 && proceedSubjects?.length !== maxAllowed &&
                            <>
                                <p>
                                    Click "Yes" to update your registration.
                                </p>
                                <p>
                                    Click "No" to proceed to exam with your current subjects.
                                </p>
                            </>
                        }
                    </div>
                ),
                buttons: [
                    {
                        label: 'Yes',
                        onClick: async () => {
                            if (!user?._id || !examId) {
                                alert.error('Missing required information.');
                                return;
                            }

                            if (!subscriptionId) {
                                history.push('/subscription-plans');
                                return;
                            }

                            const payload = {
                                userId: user._id,
                                examId: examId,
                                categoryId: categoryId ? categoryId : null,
                                courseId: courseId ? courseId : null,
                                userSubscription: subscriptionId,
                                subjects: isChecked,
                                subjectsProcessed: Array.from(new Set([...(proceedSubjects || []), ...isChecked])),
                                subjectsUpdate: [],
                                updateRequest: false
                            };

                            try {
                                await dispatch(saveExamSubjects(payload));
                                await dispatch(getSavedExamSubjects(userId));
                            } catch (error) {
                                const backendMessage = error?.message || error?.errMessage || 'This subscription has already been used. Please purchase a new subscription.';

                                confirmAlert({
                                    title: 'Subscription Required',
                                    message: (
                                        <div>
                                            <p>{backendMessage}</p>
                                            <p>Would you like to purchase another subscription now?</p>
                                        </div>
                                    ),
                                    buttons: [
                                        {
                                            label: 'Purchase Plan',
                                            onClick: () => history.push('/subscription-plans')
                                        },
                                        {
                                            label: 'Cancel',
                                            onClick: () => { }
                                        }
                                    ]
                                });

                                localStorage.removeItem('userSubscription');
                                localStorage.removeItem('subjectMsg');
                                localStorage.setItem('pendingSubjectRegistration', '/course-subjects');
                                localStorage.setItem('forceSubscriptionRenewal', 'true');
                                setSubscriptionId('');
                                history.replace('/subscription-plans');
                                return;
                            }

                            localStorage.setItem('subjects', JSON.stringify(isChecked));
                            localStorage.setItem('subjectMsg', 'Subject registered successfully.');
                            history.push('/saved-subjects');
                        },
                    },
                    {
                        label: 'No',
                        onClick: () => {
                            if (proceedSubjects?.length !== 0) {
                                localStorage.setItem('subjects', JSON.stringify(isChecked));
                                history.push('/exam-type');
                            }
                        },
                    },
                ],
            });
        } else {
            // User already has all subjects registered, proceed directly
            localStorage.setItem('subjects', JSON.stringify(isChecked));
            history.push('/exam-type');
        }
    };

    // Handle navigation to the previous page
    const goToPreviousPage = (e) => {
        e.preventDefault();
        setIsChecked('');
        if (examName === "JSCE (BECE)") {
            history.push('/exams');
        } else {
            history.push('/category-courses');
        }
    };

    return (
        <Fragment>
            {/* Page Banner Section */}
            <section
                className="page-banner-sec text-center"
                style={{
                    backgroundImage: `url("../assets/frontend/img/about-banner.jpg")`
                }}
            >
                <div className="container">
                    <ul className="clearfix paginations-cust">
                        <li>
                            <Link to="/">Home</Link>
                        </li>
                        <li>
                            <Link to="/category-courses">Intended Courses Of Study</Link>
                        </li>
                    </ul>
                    <h1>Subject of Study</h1>
                    {examSlug === "jamb-utme" ? (
                        <h3>Subject registration - select any {maximumSubjects} subjects</h3>
                    ) : (
                        <h3>Select {maximumSubjects} Subjects of Choice Required for Intended Course of Study for University/Tertiary Education</h3>
                    )}
                </div>
            </section>

            {/* Subscription Page Section */}
            <section className="subscription-page">
                <div className="container">
                    <div className="end-plan">
                        <h1>Suggested {examName} Subject Combination</h1>
                    </div>
                    <div className="custom-radios-plan custom-check-box course-spaner">
                        {csloading || sloading ? (
                            <Loader />
                        ) : (
                            <div className="wrapper clearfix">
                                {filteredSubject !== undefined && filteredSubject.length > 0 ? (
                                    filteredSubject.map((item, key) => {
                                        const indexId = key + 1;
                                        const isMandatory = item.mandetory;
                                        return (
                                            <Fragment key={indexId}>
                                                <input
                                                    type="checkbox"
                                                    name="subjects"
                                                    id={`option-${indexId}`}
                                                    value={item._id}
                                                    checked={isChecked.includes(item._id)}
                                                    onChange={handleCheckboxChange}
                                                    data-slug={item.slug}
                                                    disabled={isMandatory || proceedSubjects.includes(item._id)}
                                                />
                                                <label
                                                    htmlFor={`option-${indexId}`}
                                                    className={`option option-${indexId}`}
                                                >
                                                    <div className="dot" />
                                                    <div className="extype">
                                                        <img
                                                            src={subjectLogoUrl(item.logo)}
                                                            alt="subject logo"
                                                            onError={(e)=>{e.currentTarget.onerror=null;e.currentTarget.src=placeholderImageUrl();}}
                                                        />
                                                    </div>
                                                    <span>{item.title}{proceedSubjects.includes(item._id) || item?.mandetory && <span>Already Registered <h5 className='bg-red'>{item?.mandetory && "Mandatory"}</h5></span>}</span>
                                                </label>
                                            </Fragment>
                                        );
                                    })
                                ) : (
                                    <span className='text-center'>Subjects not found</span>
                                )}
                            </div>
                        )}
                    </div>
                    {ifRegSubjects !== undefined && ifRegSubjects !== null ?
                        <div className="next-prev-steps text-center">
                            <button className="chose-more" onClick={submitHandler}>
                                Update
                            </button>
                        </div>
                        :
                        <div className="next-prev-steps text-center">
                            <button onClick={goToPreviousPage} className="chose-more">
                                <i className="fas fa-arrow-left" /> Prev
                            </button>{' '}
                            <button
                                type="submit"
                                className="chose-more"
                                onClick={submitHandler}
                            >
                                Next <i className="fas fa-arrow-right" />
                            </button>
                        </div>}
                </div>
            </section>
        </Fragment>
    );
};

export default CourseSubjects;
